import React, { useState, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Container, InputLojas } from './styles';
import api from '~/services/api';
import useAuth from '~/hooks/useAuth';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';

interface ILoja {
  cod_loja: number;
  des_loja: string;
  des_cidade: string;
  flg_padrao: boolean;
  flg_checked: boolean;
  des_uf: string;
  num_cnpj: string;
  tipo_regime: number;
  flg_inativa: boolean;
  cod_cidade: number;
  flg_envia_ibs_cbs: boolean;
  per_cbs_teste: number;
  per_ibs_mun_teste: number;
  per_ibs_uf_teste: number;
}

interface ISelectLoja {
  value: number;
  label: string;
  uf?: string;
  selected?: boolean;
  // extras usados pelo onChangedObject (mantidos)
  des_loja?: string;
  cod_loja?: number;
  num_cnpj?: string;
  tipo_regime?: number;
  cod_cidade?: number;
  empresa?: {
    flg_envia_ibs_cbs?: boolean;
    per_cbs_teste?: number;
    per_ibs_mun_teste?: number;
    per_ibs_uf_teste?: number;
  };
}

interface ResponseLoja {
  data: ILoja[];
  success: boolean;
}

interface LojaProps {
  disabled?: boolean;
  selectedLoja?: number | Array<number>;
  resetLojas?: boolean;
  onChange(value: number | Array<number>): void;
  onChangedObject?(value: any): void;
  isMulti?: boolean;
  isDisabled?: boolean;
  IsInvalid?: boolean;
  requiredSignal?: boolean;
  showTooltipSingle?: boolean;
}

const Loja: React.FC<LojaProps> = ({
  selectedLoja,
  disabled,
  resetLojas = false,
  onChange,
  isMulti = false,
  IsInvalid = false,
  isDisabled = false,
  requiredSignal = false,
  onChangedObject,
  showTooltipSingle = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState(false);
  const [lojas, setLojas] = useState<ILoja[]>([]);
  const [optionsSelect, setOptionsSelect] = useState<ISelectLoja[]>([]);
  const [optionsSelectActive, setOptionsSelectActive] = useState<ISelectLoja[]>(
    [],
  );
  const [value, setValue] = useState<ISelectLoja | null>(null);
  const { user } = useAuth();

  // --- Estilos do react-select para manter a aparência atual (roxo) ---
  const getSelectStyles = (
    invalid: boolean,
  ): StylesConfig<ISelectLoja, false> => ({
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 6,
      borderColor: invalid
        ? '#cf1919'
        : state.isFocused
        ? '#8850bf'
        : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(136,80,191,0.20)' : 'none',
      ':hover': {
        borderColor: '#8850bf',
      },
      backgroundColor: isDisabled ? 'rgba(0,0,0,0.04)' : base.backgroundColor,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    }),
    placeholder: (base) => ({
      ...base,
      opacity: 0.8,
    }),
    singleValue: (base) => ({
      ...base,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#57069e'
        : state.isFocused
        ? 'rgba(136,80,191,0.08)'
        : 'transparent',
      color: state.isSelected ? '#ffffff' : '#1d1d1f',
      ':active': {
        backgroundColor: state.isSelected ? '#57069e' : 'rgba(136,80,191,0.12)',
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: 8,
      overflow: 'hidden',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
  });

  const selectTheme: any = (theme: any) => ({
    ...theme,
    borderRadius: 6,
    colors: {
      ...theme.colors,
      primary: '#8850bf',
      primary25: 'rgba(136,80,191,0.12)',
      primary50: 'rgba(136,80,191,0.20)',
      danger: '#cf1919',
      neutral20: '#d1d5db',
      neutral30: '#8850bf',
    },
  });

  useEffect(() => {
    if (resetLojas) {
      if (lojas.length === 1) {
        return handleSetAllLojas();
      }
      const arrayLojas = lojas.map((loja) => ({ ...loja })); // cópia segura

      arrayLojas.forEach((loja, index) => {
        arrayLojas[index].flg_checked = loja.flg_padrao === true;
      });

      setAll(false);
      setLojas(arrayLojas);

      const newValue: Array<number | undefined> = [];
      newValue.push(user?.loja);

      const arrayFiltrado: number[] = newValue.filter(
        (n: number | undefined): n is number => typeof n === 'number',
      );
      emitChange(arrayFiltrado);
    }
  }, [resetLojas]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetAllLojas = () => {
    if (!all) {
      setAll(true);
      const newLojas = lojas.map((l: ILoja) => ({
        ...l,
        flg_checked: true,
      }));
      setLojas(newLojas);
      const newValue = newLojas.map((l: ILoja) => l.cod_loja);
      emitChange(newValue);
    } else {
      setAll(false);
      const newLojas = lojas.map((l: ILoja) => ({
        ...l,
        flg_checked: l.cod_loja === Number(user?.loja),
      }));
      setLojas(newLojas);
      emitChange(isMulti ? [newLojas[0].cod_loja] : []);
    }
  };

  const lastEmittedRef = React.useRef<number[] | number | null>(null);

  const emitChange = (values: number[] | number) => {
    if (JSON.stringify(lastEmittedRef.current) === JSON.stringify(values)) {
      return;
    }

    lastEmittedRef.current = values;
    onChange(values);
  };

  const onChangeValue = (id: number) => {
    const newLojas = lojas.map((l: ILoja) => {
      if (l.cod_loja === id) {
        return { ...l, flg_checked: !l.flg_checked };
      }
      return l;
    });

    const existeLojaSelecionada = newLojas.some(
      (loja) => loja.flg_checked === true,
    );

    if (existeLojaSelecionada) {
      const isAnyLojaSelected = newLojas.find(
        (bool) => bool.flg_checked === true,
      );
      if (!isAnyLojaSelected) setAll(false);

      const isAnyLojaUnSelected = newLojas.find(
        (bool) => bool.flg_checked === false,
      );
      if (!isAnyLojaUnSelected) {
        setAll(true);
      } else {
        setAll(false);
      }

      setLojas(newLojas);
      const filteredLojas = newLojas.filter((l: ILoja) => l.flg_checked);
      const newValue = filteredLojas.map((l: ILoja) => l.cod_loja);

      // Filtrar as lojas com base nos códigos fornecidos
      const lojasFiltradas = lojas.filter((loja: any) =>
        newValue.includes(loja.cod_loja),
      );
      if (onChangedObject) onChangedObject(lojasFiltradas);
      emitChange(newValue);
    } else {
      toast.warning('Ao menos uma loja deve estar selecionada');
    }
  };

  const onChangeValueSimple = (opt: any) => {
    if (opt) {
      setValue(opt);
      emitChange(opt.value);
      if (onChangedObject) onChangedObject(opt);
    }
  };

  const getLojas = useCallback(async () => {
    setLoading(true);
    try {
      if (user && user.id) {
        const { data } = await api.get<ResponseLoja>(`/lojas/usuario`);

        if (
          Array.isArray(selectedLoja) &&
          selectedLoja.length === data.data.length
        ) {
          setAll(true);
        }

        if (data.success) {
          if (isMulti) {
            const newLojas = (() => {
              // Verifica se selectedLoja corresponde a algum cod_loja das lojas
              const isSelectedLojaPresent = data.data.some((l) =>
                Array.isArray(selectedLoja)
                  ? selectedLoja.includes(l.cod_loja)
                  : selectedLoja === l.cod_loja,
              );

              return data.data
                .filter((l: ILoja) => l.flg_inativa !== true)
                .map((l: ILoja) => {
                  let isSelected = false;

                  if (isSelectedLojaPresent) {
                    // Se selectedLoja corresponder a algum cod_loja, marca apenas essas lojas
                    isSelected = Array.isArray(selectedLoja)
                      ? selectedLoja.includes(l.cod_loja)
                      : selectedLoja === l.cod_loja;
                  } else {
                    // Se nenhum cod_loja corresponder a selectedLoja, marca a loja padrão
                    isSelected = l.flg_padrao;
                  }

                  return {
                    ...l,
                    flg_checked: isSelected,
                  };
                });
            })();

            setLojas(newLojas);
            const filteredLojas = newLojas.filter((l: ILoja) => l.flg_checked);
            const newValue = filteredLojas.map((l: ILoja) => l.cod_loja);
            emitChange(newValue);
          } else {
            const createOption = (
              loja: ILoja,
              selected: boolean,
            ): ISelectLoja => ({
              label:
                loja.cod_loja < 10
                  ? `0${loja.cod_loja} - ${loja.des_loja} (${loja.des_cidade})`
                  : `${loja.cod_loja} - ${loja.des_loja} (${loja.des_cidade})`,
              value: loja.cod_loja,
              selected,
              des_loja: loja.des_loja,
              uf: loja.des_uf,
              cod_loja: loja.cod_loja,
              num_cnpj: loja.num_cnpj,
              tipo_regime: loja.tipo_regime,
              cod_cidade: loja.cod_cidade,
              empresa: {
                flg_envia_ibs_cbs: loja?.flg_envia_ibs_cbs || false,
                per_cbs_teste: loja?.per_cbs_teste || 0,
                per_ibs_mun_teste: loja?.per_ibs_mun_teste || 0,
                per_ibs_uf_teste: loja?.per_ibs_uf_teste || 0,
              },
            });

            const options: ISelectLoja[] = [];
            const optAtivo: ISelectLoja[] = [];

            data.data.forEach((loja: ILoja) => {
              const selected = loja.cod_loja === selectedLoja;
              if (loja.flg_inativa !== true) {
                optAtivo.push(createOption(loja, selected));
              }
              options.push(createOption(loja, selected));

              if (selected) {
                setValue({ value: loja.cod_loja, label: loja.des_loja });
              }
            });

            setOptionsSelect(options);
            setOptionsSelectActive(optAtivo);

            const opt = options.find((o) => o.selected === true);
            if (opt) emitChange(opt.value);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isMulti, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLojas();
  }, [getLojas]);

  useEffect(() => {
    const loja = optionsSelect.find((lj) => lj.value === selectedLoja);
    if (loja) {
      setValue(loja);
    } else {
      setValue(null);
    }
  }, [optionsSelect, selectedLoja]);

  useEffect(() => {
    if (lojas.length === 1) {
      setAll(true);
    }
  }, [lojas]);

  useEffect(() => {
    if (!Array.isArray(selectedLoja)) return;

    setLojas((prev) => {
      const mudou = prev.some(
        (l) => l.flg_checked !== selectedLoja.includes(l.cod_loja),
      );

      if (!mudou) return prev;

      return prev.map((l) => ({
        ...l,
        flg_checked: selectedLoja.includes(l.cod_loja),
      }));
    });
  }, [selectedLoja]);

  return (
    <Container>
      <div className="row">
        {/* Lista de Lojas */}
        <div className="col-12">
          <label
            htmlFor="input-lojas"
            className="sc-iqAclL lflqij css-1bwberi-Label"
            style={{ marginBottom: !isMulti ? '4.5px' : '0px' }}
          >
            {isMulti
              ? requiredSignal
                ? 'Lojas *'
                : 'Lojas'
              : requiredSignal
              ? 'Loja *'
              : 'Loja'}
          </label>

          {isMulti && (
            <InputLojas id="input-lojas" IsInvalid={IsInvalid}>
              {loading && (
                <CircularProgress
                  size={15}
                  style={{ color: '#8850bf', margin: '10px 0px 0px 10px' }}
                />
              )}

              {!loading && (
                <Tippy
                  placement="top"
                  content="TODAS AS LOJAS"
                  arrow
                  animation="scale"
                >
                  <div
                    onClick={() => {
                      if (!disabled) handleSetAllLojas();
                    }}
                  >
                    <Form.Check
                      className="ButtonLoja"
                      type="checkbox"
                      label="Todas"
                      disabled={disabled}
                      checked={all}
                      onChange={() => null}
                      style={{
                        backgroundColor: all ? '#57069e' : '#8850bf',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </div>
                </Tippy>
              )}

              {lojas &&
                !loading &&
                lojas.map((loja: any) => {
                  return (
                    <Tippy
                      placement="top"
                      content={`LOJA ${loja.cod_loja} - ${loja.des_loja}`}
                      key={loja.cod_loja}
                      arrow
                      animation="scale"
                    >
                      <div
                        onClick={() => {
                          if (!disabled) onChangeValue(loja.cod_loja);
                        }}
                      >
                        <Form.Check
                          className="ButtonLoja"
                          type="checkbox"
                          disabled={disabled}
                          label={`${loja.cod_loja}`}
                          checked={loja.flg_checked}
                          onChange={() => null}
                          style={{
                            backgroundColor: loja.flg_checked
                              ? '#57069e'
                              : '#8850bf',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                      </div>
                    </Tippy>
                  );
                })}
            </InputLojas>
          )}

          {!isMulti && showTooltipSingle && (
            <Tippy
              content={
                loading
                  ? 'Buscando loja'
                  : `LOJA ${value?.value} - ${value?.label}`
              }
              placement="top"
              arrow
              animation="scale"
            >
              <div
                style={{
                  borderBottom: IsInvalid ? '2px solid #cf1919' : 'none',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Select
                  className={isDisabled === true ? 'isDisabled' : ''}
                  options={optionsSelectActive}
                  inputId="loja"
                  value={value || null}
                  placeholder="Selecione a Loja"
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                  onChange={onChangeValueSimple}
                  isDisabled={isDisabled}
                  styles={getSelectStyles(!!IsInvalid)}
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </Tippy>
          )}

          {!isMulti && !showTooltipSingle && (
            <div
              style={{
                borderBottom: IsInvalid ? '2px solid #cf1919' : 'none',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              <Select
                className={isDisabled === true ? 'isDisabled' : ''}
                options={optionsSelectActive}
                inputId="loja"
                value={value || null}
                placeholder="Selecione a Loja"
                noOptionsMessage={() => 'Nenhum registro encontrado'}
                onChange={onChangeValueSimple}
                isDisabled={isDisabled}
                styles={getSelectStyles(!!IsInvalid)}
                theme={selectTheme}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default Loja;
